// client/src/pages/sections/groups/RgCompareCards.js
// Phase 3 comparison panel — renders an RG's composition for the partner.
// Given an anchor SG code, fetches /api/dgRgMembers/{anchorCode} and
// displays the RG → SGs → clients hierarchy as a stack of SG cards.
//
// Used inside an expanded DQ issue row when the issue has a clear anchor:
//   RG_NEEDS_CONFIRMATION   anchor = primary_client_code
//   RG_SUGGESTED_BY_SG      anchor = primary_client_code (SG parent)
// For RG_SUGGESTED_BY_BG (no single anchor yet) the regular GroupBox
// continues to render the cluster member list.

import { useCallback, useEffect, useRef, useState } from 'react';
import MoveSgToRgButton from './MoveSgToRgButton';
import { postResolveAction } from './resolveActionQueue';
import { emitToast, errorText } from './toast';
import ClientChip, { rememberClientNames } from './ClientChip';

export default function RgCompareCards({
  anchorCode,
  // When true, each non-anchor Signer Group card gets a "Move to another
  // Relationship Group" action (Step 4 "needs confirmation" review only).
  editable = false,
  // When true, each non-anchor Signer Group card also gets a "Make RG
  // anchor" action. Only enabled in the confirm flow (the anchor partner
  // owns the RG); the server also enforces anchor-partner authorization.
  canReanchor = false,
  // Caller identity — a partner may only move a Signer Group they partner
  // (admins may move any). When unset, no per-SG restriction is applied.
  viewerEmail,
  isAdmin = false,
  // Bubbles up after a move so the section can refetch the issue list.
  onResolved,
  testMode,
  testSessionId,
}) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);

  // Mirror of the rendered composition — optimistic actions snapshot THIS (what the
  // user is looking at), never the inside of a setState updater.
  const dataRef = useRef(null);
  dataRef.current = data;
  const inFlight = useRef(0);       // optimistic actions queued / in flight
  const dirty    = useRef(false);   // a failure we could not revert cleanly (others in flight)
  const reqId    = useRef(0);       // latest fetch wins

  // `silent` = background reconcile after an action: no loading flag, failure keeps
  // the composition on screen (the parent's refetch will drop the row if the group is gone).
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!anchorCode) return;
    const mine = ++reqId.current;
    if (!silent) {
      setLoading(true);
      setErr(null);
    }
    try {
      const r = await fetch(`/api/dgRgMembers/${encodeURIComponent(anchorCode)}`, { credentials: 'include' });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const json = await r.json();
      rememberClientNames(json);
      if (mine === reqId.current) setData(json);
    } catch (e) {
      if (!silent && mine === reqId.current) setErr(e.message);
    } finally {
      if (!silent && mine === reqId.current) setLoading(false);
    }
  }, [anchorCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  // Optimistic action runner for the per-SG buttons (move / re-anchor).
  //   patch(prev) → the composition as it will look once the action lands (applied now)
  //   task()      → the POST; a rejection reverts the patch and re-throws so the
  //                 button can toast the server's error
  //   reload      → quietly re-read this composition on success (skip it when the
  //                 action re-keys the RG, e.g. re-anchoring: the parent refetch
  //                 re-mounts the row on the new anchor)
  // If several actions overlap, a failure can't blindly restore its own snapshot (it
  // would undo a later patch), so the composition is re-read once the queue drains.
  const mutate = useCallback(async ({ patch, task, reload = false }) => {
    const before = dataRef.current;
    inFlight.current += 1;
    setData(prev => (prev ? patch(prev) : prev));
    let result;
    let failed = null;
    try {
      result = await task();
    } catch (e) {
      failed = e;
    }
    inFlight.current -= 1;
    if (failed) {
      if (inFlight.current > 0) dirty.current = true;
      else if (!dirty.current) setData(before);
    }
    if (inFlight.current === 0) {
      const needResync = (!failed && reload) || dirty.current;
      dirty.current = false;
      if (needResync) await load({ silent: true });
      if (!failed || needResync) { if (onResolved) await onResolved(result); }
    }
    if (failed) throw failed;
    return result;
  }, [load, onResolved]);

  // Full-panel "Loading…" only on the FIRST fetch (no data yet). Actions no longer
  // refetch with a blocking overlay — they patch the composition in place.
  if (loading && !data) return <div className="cg-rg-loading">Loading relationship group composition…</div>;
  if (err && !data)     return <div className="cg-rg-error">Couldn't load Relationship Group: {err}</div>;
  if (!data)            return null;

  const totalSgs    = (data.sgs || []).length;
  const totalClients = data.totalMembers || 0;

  return (
    <div className="cg-rg-panel">
      <div className="cg-rg-panel-header">
        <div className="cg-rg-panel-title">
          <span className="cg-rg-panel-label">Relationship Group</span>
          <ClientChip code={data.anchor.sgCode} name={data.anchor.sgName} />
        </div>
        <div className="cg-rg-panel-meta">
          {totalClients} client{totalClients === 1 ? '' : 's'} across{' '}
          {totalSgs} signer group{totalSgs === 1 ? '' : 's'}
          {data.truncated && (
            <span className="cg-rg-truncated"> · showing first {data.maxMembers}</span>
          )}
        </div>
      </div>

      <div className="cg-rg-sg-list">
        {totalSgs === 0 && (
          <p className="cg-resolve-empty">No signer groups remain in this Relationship Group.</p>
        )}
        {(data.sgs || []).map((sg) => (
          <SgCard
            key={sg.sgCode}
            sg={sg}
            rgAnchor={data.anchor.sgCode}
            editable={editable}
            canReanchor={canReanchor}
            // When the RG has a single signer group, the anchor SG can be
            // moved out — that just deprecates the old single-SG RG.
            soleSg={(data.sgs || []).length === 1}
            viewerEmail={viewerEmail}
            isAdmin={isAdmin}
            mutate={mutate}
            testMode={testMode}
            testSessionId={testSessionId}
          />
        ))}
      </div>
    </div>
  );
}

function SgCard({ sg, rgAnchor, editable, canReanchor, soleSg, viewerEmail, isAdmin, mutate, testMode, testSessionId }) {
  // The RG anchor's own Signer Group normally can't be moved out — that
  // would strand the rest of the RG behind a dangling anchor (the server
  // rejects it). EXCEPTION: when it's the RG's only signer group, moving it
  // just deprecates the old single-SG RG, so it's allowed.
  // A partner may only move a Signer Group they actually partner (any member
  // routed to them); admins may move any. When viewerEmail is unset (e.g.
  // firmwide admin view) the per-SG partner restriction is skipped.
  const viewer = (viewerEmail || '').toLowerCase();
  const viewerPartnersSg = !viewer || (sg.members || []).some(
    m => (m.partnerEmail || '').toLowerCase() === viewer
  );
  const movableAnchor = !sg.isAnchor || soleSg;
  const canMove = editable && movableAnchor && (isAdmin || viewerPartnersSg);
  return (
    <div className={`cg-rg-sg-card ${sg.isAnchor ? 'cg-rg-sg-card--anchor' : ''}`}>
      <div className="cg-rg-sg-header">
        <span className="cg-rg-sg-label">Signer Group</span>
        <ClientChip code={sg.sgCode} name={sg.sgName} />
        {sg.isAnchor && (
          <span className="cg-rg-anchor-badge" title="Relationship Group anchor">★ anchor</span>
        )}
        <span className="cg-rg-sg-count">
          {sg.members.length} member{sg.members.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="cg-rg-member-list">
        {sg.members.map((m) => (
          <li key={m.clientCode} className="cg-rg-member-row">
            <ClientChip code={m.clientCode} name={m.clientName} />
            {m.entityType && (
              <span className="cg-rg-member-entity">· {m.entityType}</span>
            )}
            {m.isRgAnchor && (
              <span className="cg-rg-member-tag cg-rg-member-tag--anchor">Relationship Group anchor</span>
            )}
          </li>
        ))}
        {/* Actions live in the same container as the client list, below the
            members — stacked vertically and right-oriented. */}
        {(canMove || (canReanchor && !sg.isAnchor)) && (
          <li className="cg-rg-sg-actions">
            {canMove && (
              <MoveSgToRgButton
                sg={{ sgCode: sg.sgCode, sgName: sg.sgName }}
                currentRgAnchor={rgAnchor}
                // Hide "create a new Relationship Group" when this is the only
                // signer group AND it has a single client — it's already its
                // own group, so making a new one would be a no-op.
                allowMakeOwn={!(soleSg && (sg.members || []).length === 1)}
                mutate={mutate}
                testMode={testMode}
                testSessionId={testSessionId}
              />
            )}
            {/* Re-anchoring the RG onto a different signer group. Only the
                anchor partner may do this (server-enforced); offered on
                non-anchor signer groups in the confirm flow. */}
            {canReanchor && !sg.isAnchor && (
              <MakeAnchorButton
                sg={sg}
                mutate={mutate}
                testMode={testMode}
                testSessionId={testSessionId}
              />
            )}
          </li>
        )}
      </ul>
    </div>
  );
}

// "Make RG anchor" — re-points the whole Relationship Group onto this signer
// group via the reanchor_rg verb. Direct action (no modal); the server
// enforces that only the current anchor's partner (or an admin) may do it.
// Optimistic: the ★ anchor badge moves immediately; `mutate` reverts it and we
// toast the server's error if the call is refused.
function MakeAnchorButton({ sg, mutate, testMode, testSessionId }) {
  const sgCode = sg.sgCode;
  function go(e) {
    e.stopPropagation();
    const patch = (prev) => ({
      ...prev,
      anchor: { ...prev.anchor, sgCode, sgName: sg.sgName || prev.anchor.sgName },
      sgs: (prev.sgs || []).map(x => ({
        ...x,
        isAnchor: x.sgCode === sgCode,
        members: x.members.map(m => ({ ...m, isRgAnchor: m.clientCode === sgCode })),
      })),
    });
    mutate({
      patch,
      task: () => postResolveAction({
        verb: 'reanchor_rg',
        clientCode: sgCode,
        payload: {},
        testMode: Boolean(testMode),
        sessionId: testMode ? testSessionId : null,
      }),
    })
      .then((data) => {
        emitToast({ kind: 'success', message: (data && data.message) || `Relationship Group re-anchored on ${sgCode}.` });
      })
      .catch((err) => {
        emitToast({ kind: 'error', message: `Couldn't re-anchor: ${errorText(err)}. Restored.` });
      });
  }
  return (
    <button
      type="button"
      className="cg-move-btn"
      onClick={go}
      title={`Make signer group ${sgCode} the anchor of this Relationship Group.`}
    >
      Make RG anchor
    </button>
  );
}
